import type { Session } from '@supabase/supabase-js';
import { Button, PageTitle } from '@nuberu/ui';
import { useState, type ReactElement } from 'react';

import { AiUsagePanel } from '@/ai-usage/AiUsagePanel';
import { useSession } from '@/auth/use-session';
import { getSupabaseClient } from '@/lib/supabase';
import { LookupPanel } from '@/lookup/LookupPanel';
import { OperatorsPanel } from '@/operators/OperatorsPanel';
import { PaymentsPanel } from '@/payments/PaymentsPanel';
import { QueuesPanel } from '@/queues/QueuesPanel';
import { LoginScreen } from '@/screens/LoginScreen';
import { PasswordAlertsPanel } from '@/password-alerts/PasswordAlertsPanel';
import { SystemPanel } from '@/screens/SystemPanel';

type Tab = 'system' | 'payments' | 'ai' | 'queues' | 'operators' | 'lookup' | 'passwords';

const TABS: readonly { id: Tab; label: string }[] = [
  { id: 'system', label: 'Sistema' },
  { id: 'payments', label: 'Pagos sin reserva' },
  { id: 'ai', label: 'IA · uso' },
  { id: 'queues', label: 'Colas' },
  { id: 'operators', label: 'Operadores' },
  { id: 'lookup', label: 'Soporte' },
  { id: 'passwords', label: 'Contraseñas' },
];

// The signed-in backoffice shell: shared header (identity + sign-out) and tab
// nav, with the active tab's panel below. Each panel is pure content — the shell
// owns the page chrome so the tabs stay consistent as the backoffice grows.
const Backoffice = ({ session }: { session: Session }): ReactElement => {
  // The alert emails link to /?tab=pagos: open that tab straight away.
  const [tab, setTab] = useState<Tab>(() =>
    new URLSearchParams(window.location.search).get('tab') === 'pagos' ? 'payments' : 'system',
  );
  const email = typeof session.user.email === 'string' ? session.user.email : '';

  const signOut = (): void => {
    void getSupabaseClient().auth.signOut();
  };

  return (
    <div className="min-h-screen bg-surface-2 p-4 sm:p-8">
      <div className="mx-auto flex max-w-3xl flex-col gap-6">
        <header className="flex items-center justify-between gap-4">
          <div>
            <PageTitle className="text-text-primary">Backoffice</PageTitle>
            <p className="text-sm text-text-secondary">{email}</p>
          </div>
          <Button variant="neutral" size="sm" onClick={signOut}>
            Cerrar sesión
          </Button>
        </header>

        <nav className="flex items-center gap-1">
          {TABS.map((entry) => (
            <Button
              key={entry.id}
              variant={entry.id === tab ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => setTab(entry.id)}
            >
              {entry.label}
            </Button>
          ))}
        </nav>

        {tab === 'system' ? (
          <SystemPanel session={session} />
        ) : tab === 'passwords' ? (
          <PasswordAlertsPanel session={session} />
        ) : tab === 'payments' ? (
          <PaymentsPanel session={session} />
        ) : tab === 'ai' ? (
          <AiUsagePanel session={session} />
        ) : tab === 'queues' ? (
          <QueuesPanel session={session} />
        ) : tab === 'operators' ? (
          <OperatorsPanel session={session} />
        ) : (
          <LookupPanel session={session} />
        )}
      </div>
    </div>
  );
};

// Three states, one gate. While the initial getSession() is in flight we render
// a neutral loading screen rather than flashing the login form; `null` means
// signed out; a real Session hands off to the backoffice shell.
//
// NOTE: signing in is necessary but NOT sufficient — authorisation is enforced
// server-side by the platform-admin allowlist. A signed-in non-admin reaches the
// panels and sees an "access denied" state from the API's 403, never data.
export const App = (): ReactElement => {
  const session = useSession();

  if (session === undefined) {
    return (
      <div className="grid min-h-screen place-items-center bg-surface-2">
        <p className="text-sm text-text-secondary">Cargando…</p>
      </div>
    );
  }

  if (session === null) {
    return <LoginScreen />;
  }

  return <Backoffice session={session} />;
};
